import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Card } from "../../../components/Cards/Card";
import { useTranslation } from "react-i18next";
import overlayFactory from "react-bootstrap-table2-overlay";
import { Add, Remove } from "../../../components/Icons/Icons";
import BootstrapTable from "react-bootstrap-table-next";
import { Tooltip } from "../../../components/Utils/Tooltips";
import { dateFormatter } from "../../../components/Utils/Formatter";
import { toast } from "react-toastify";
import { ModalAssignDeviceToStartup } from "../../../components/ClinicalTrials/ModalAssignDeviceToStartup";
import {
  addStartupDevice,
  getAttachmentEndpoint,
  getStartupDevices,
  removeStartupDevice,
} from "../../../api/clinicalTrial";
import { HandleApiError } from "../../../components/Notifications/APIErrorHandler";
import { attachmentEntityType } from "../../../variables/Enums";
import { useFiles } from "../../../components/CustomControls/Files/Hooks";
import { WithAuth } from "../../../components/Authorization/WithAuth";
import { ModalConfirm } from "../../../components/Modals/ModalConfirm";
import { DocumentViewer } from "../../../components/CustomControls/DocViewer/DocumentViewer";
import { decodeFileName } from "../../../components/Utils/Commons";

export const StartupDevices = (startup) => {
  const [response, downloadBlob] = useFiles(
    getAttachmentEndpoint(attachmentEntityType.Hospital)
  );
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [removeId, setRemoveId] = useState(false);

  useEffect(() => {
    const fetchData = () => {
      setIsLoading(true);
      getStartupDevices(startup.startup.id)
        .then((response) => {
          setData(response.data);
          setIsLoading(false);
        })
        .catch((error) => {
          HandleApiError(error);
          setIsLoading(false);
        });
    };

    fetchData();
  }, []);

  const handleModalClose = (refresh) => {
    setShowAddModal(false);

    if (refresh) {
      getStartupDevices(startup.startup.id)
        .then((response) => {
          setData(response.data);
          setIsLoading(false);
        })
        .catch((error) => {
          HandleApiError(error);
          setIsLoading(false);
        });
    }
  };

  const handlePreviewFile = async (attachmentId) => {
    await downloadBlob(attachmentId);
  };

  const columns = [
    {
      dataField: "name",
      text: t("devices.name"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "name",
      text: t("devices.serialNumber"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "attachmentName",
      text: t("devices.attachment.name"),
      headerStyle: { width: "200px" },
      formatter: (_, row) => {
        if (!row.attachmentName) {
          return "";
        }
        return (
          <a
            href="javascript:void(0)"
            onClick={() => handlePreviewFile(row.attachmentId)}
          >
            {decodeFileName(row.attachmentName)}
          </a>
        );
      },
    },
    {
      dataField: "attachmentDueDate",
      text: t("devices.attachment.dueDate"),
      // sort: true,
      headerStyle: { width: "200px" },
      formatter: (_, row) => {
        if (!row.attachmentDueDate) {
          return "";
        }
        return dateFormatter(row.attachmentDueDate, "DD/MM/YY");
      },
    },
    {
      dataField: "description",
      text: t("devices.description"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "view",
      text: "",
      isDummyField: true,
      headerClasses: "header-protocol-actions",
      classes: "row-protocol-actions",
      headerStyle: { width: "100px" },
      headerAlign: "center",
      align: "center",
      formatter: (_cell, row, rowIndex) => (
        <div key={rowIndex}>
          <WithAuth requiredPermission={["Devices.Delete"]}>
            <button
              className="btn-table-actions"
              onClick={() => setRemoveId(row.id)}
            >
              <Tooltip
                tooltip={t("devices.startup.removeDeviceTooltip")}
                id={"tooltipedit-" + row.id}
              >
                <span aria-hidden="true">
                  <Remove />
                </span>
              </Tooltip>
            </button>
          </WithAuth>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card
        header={
          <Row>
            <Col md={2}>
              <WithAuth requiredPermission={["Startups.Edit"]}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowAddModal(true);
                  }}
                >
                  <Add />
                  {t("devices.startup.addDevice")}
                </button>
              </WithAuth>
            </Col>
          </Row>
        }
        content={
          <BootstrapTable
            bordered={false}
            hover
            remote
            keyField="id"
            data={data}
            columns={columns}
            classes="table-spaced table-spaced-small"
            wrapperClasses="table-responsive"
            noDataIndication={() => <>{t("commons.noData")}</>}
            loading={isLoading}
            overlay={overlayFactory({ spinner: true })}
          />
        }
      />
      {showAddModal && (
        <ModalAssignDeviceToStartup
          startupId={startup.startup.id}
          show={showAddModal}
          onClose={handleModalClose}
          assignedDeviceIds={data.map((d) => d.id)}
          onSave={async (payload, setSaving) => {
            try {
              const { deviceIds } = payload;
              let allSuccess = true;

              for (const deviceId of deviceIds) {
                try {
                  const result = await addStartupDevice(
                    startup.startup.id,
                    deviceId
                  );
                  if (result.status !== 200) {
                    allSuccess = false;
                  }
                } catch (error) {
                  allSuccess = false;
                  HandleApiError(error);
                }
              }

              if (allSuccess) {
                handleModalClose(true);
              } else {
                toast.error(t("errors.serverError"));
                handleModalClose(true); // Refresh para mostrar los que sí se agregaron
              }
            } finally {
              setSaving(false);
            }
          }}
        />
      )}
      {removeId && (
        <ModalConfirm
          onConfirm={async () => {
            try {
              const removeResponse = await removeStartupDevice(
                startup.startup.id,
                removeId
              );
              if (removeResponse.status == 200) {
                handleModalClose(true);
              } else {
                toast.error(t("errors.serverError"));
              }
            } catch (error) {
              HandleApiError(error);
            } finally {
              setRemoveId(undefined);
            }
          }}
          onCancel={() => setRemoveId(undefined)}
          title={t("devices.startup.removeConfirmationTitle")}
          description={t("devices.startup.removeConfirmationMessage")}
          confirmStyle="danger"
        />
      )}
      <DocumentViewer
        file={response?.blob}
        fileName={response?.fileName}
        inModal={true}
        isLoading={false}
      />
    </>
  );
};

export default StartupDevices;
